import React, { useState } from "react";
import styled from "styled-components";
import { IconButton, CircularProgress } from "@mui/material";
import { CloseIcon } from "../../../assets/icons";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
} from "./SelectTemplateStyled";
import { combinePackageForSigning } from "../../../service/settings";

const Body = styled.div`
  padding: 0 24px 16px;
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #065f46;
  font-size: 14px;
  font-weight: 600;
`;

const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
  max-height: 240px;
  overflow-y: auto;
`;

const DocItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: #eff6ff;
  }
`;

const DocName = styled.span`
  font-weight: 500;
  color: #111827;
  flex: 1;
`;

const DocMeta = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const ErrorList = styled.div`
  margin-bottom: 12px;
`;

const ErrorItem = styled.div`
  padding: 6px 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 12px;
  color: #991b1b;
  margin-bottom: 4px;
`;

const Footer = styled.div`
  padding: 12px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Btn = styled.button`
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? "#0C53BB" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#374151")};
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 12px;
`;

export default function PackageMergeSuccessModal({
  open,
  result,
  onClose,
  onSendForSignature,
}) {
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [combining, setCombining] = useState(false);

  if (!open || !result) return null;

  const { package: pkg, documents = [], errors = [] } = result;

  const allDocIds = documents.map((d) => d.id);

  const toggleDoc = (docId) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    );
  };

  const toggleAll = () => {
    if (selectedDocIds.length === allDocIds.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds([...allDocIds]);
    }
  };

  const handleSendForSignature = async () => {
    if (selectedDocIds.length === 0 || !pkg) return;
    setCombining(true);
    try {
      const matterId = documents[0]?.matter;
      const res = await combinePackageForSigning(pkg.id, {
        document_ids: selectedDocIds,
        matter_id: matterId,
      });
      const combinedDoc = res?.data?.document;
      if (combinedDoc) {
        onSendForSignature?.(combinedDoc);
      }
    } catch (err) {
      console.error("Failed to combine for signing:", err);
    } finally {
      setCombining(false);
    }
  };

  return (
    <Overlay>
      <Modal>
        <CloseButton onClick={onClose}>
          <IconButton size="small">
            <CloseIcon />
          </IconButton>
        </CloseButton>

        <Header>Package Merge Complete</Header>

        <Body>
          <SuccessBanner>
            {documents.length} document{documents.length !== 1 ? "s" : ""}{" "}
            generated successfully
          </SuccessBanner>

          {errors.length > 0 && (
            <ErrorList>
              {errors.map((err, i) => (
                <ErrorItem key={i}>
                  {err.template}: {err.error}
                </ErrorItem>
              ))}
            </ErrorList>
          )}

          {documents.length > 0 && (
            <>
              <SubText>
                Select documents to combine and send for e-signature:
              </SubText>
              <DocItem
                as="div"
                style={{ background: "#f3f4f6", cursor: "pointer" }}
                onClick={toggleAll}
              >
                <input
                  type="checkbox"
                  checked={selectedDocIds.length === allDocIds.length}
                  readOnly
                />
                <DocName>Select All</DocName>
              </DocItem>
              <DocList>
                {documents.map((doc) => (
                  <DocItem key={doc.id} onClick={() => toggleDoc(doc.id)}>
                    <input
                      type="checkbox"
                      checked={selectedDocIds.includes(doc.id)}
                      readOnly
                    />
                    <DocName>{doc.name}</DocName>
                    <DocMeta>{doc.file_type?.toUpperCase()}</DocMeta>
                  </DocItem>
                ))}
              </DocList>
            </>
          )}
        </Body>

        <Footer>
          <Btn onClick={onClose}>Close</Btn>
          {documents.length > 0 && (
            <Btn
              $primary
              disabled={selectedDocIds.length === 0 || combining}
              onClick={handleSendForSignature}
            >
              {combining ? (
                <>
                  <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />{" "}
                  Combining...
                </>
              ) : (
                `Send for Signature (${selectedDocIds.length})`
              )}
            </Btn>
          )}
        </Footer>
      </Modal>
    </Overlay>
  );
}
